import { createStore, Mutate, StoreApi } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { hasWindow } from 'utils/browser.utils';
import { isLocal } from 'utils/env.utils';

type NofomoStoreState = {
  _hasHydrated: boolean;
  afterSignInUrl: string | null;
};

type NofomoStoreActions = {
  setHasHydrated: (state: boolean) => void;
  setAfterSignInUrl: (url: string) => void;
  replaceAfterSignInUrl: (url: string) => void;
  resetAfterSignInUrl: () => void;
};

export type NofomoStore = NofomoStoreState & { actions: NofomoStoreActions };

export const initNofomoStore = (): NofomoStoreState => ({
  _hasHydrated: false,
  afterSignInUrl: null,
});

export type NofomoStoreReturnType = ReturnType<typeof createNofomoStore>;

type StoreWithPersist = Mutate<
  StoreApi<NofomoStore>,
  [
    ['zustand/immer', unknown],
    ['zustand/persist', Pick<NofomoStoreState, 'afterSignInUrl'>],
  ]
>;

const withStorageDOMEvents = (store: StoreWithPersist) => {
  const storageEventCallback = (e: StorageEvent) => {
    if (e?.key === store?.persist?.getOptions?.()?.name && e?.newValue) {
      store.persist.rehydrate();
    }
  };

  if (hasWindow()) {
    window.addEventListener('storage', storageEventCallback);

    return () => {
      window.removeEventListener('storage', storageEventCallback);
    };
  }
};

export const createNofomoStore = (
  initState: NofomoStoreState = initNofomoStore(),
) => {
  const store = createStore<NofomoStore>()(
    devtools(
      immer(
        persist(
          (set) => ({
            ...initState,
            actions: {
              setHasHydrated: (value) => {
                set((state) => {
                  state._hasHydrated = value;
                });
              },
              // Only set the after sign in url if it's not set yet
              setAfterSignInUrl: (url) => {
                set((state) => {
                  state.afterSignInUrl = url;
                });
              },
              replaceAfterSignInUrl: (url) => {
                set((state) => {
                  state.afterSignInUrl = url;
                });
              },
              resetAfterSignInUrl: () => {
                set((state) => {
                  state.afterSignInUrl = null;
                });
              },
            },
          }),
          {
            // Funny name for nofomo-store
            name: 'nf-global',
            partialize: (state) => ({
              afterSignInUrl: state.afterSignInUrl,
            }),
            onRehydrateStorage: (state) => {
              return () => state.actions.setHasHydrated(true);
            },
          },
        ),
      ),
      { name: 'nf', serialize: true, enabled: isLocal },
    ),
  );
  // Sync the store across tabs
  withStorageDOMEvents(store);
  return store;
};
